/** E-mails société : envoi, textes, rappels. */
export type MailKind = "invoice" | "quote" | "credit_note" | "reminder" | "payment";

export type MailTemplate = {
  subject: string;
  body: string;
};

export type MailReminder = {
  enabled: boolean;
  days: number;
};

export type CompanyMail = {
  from_name: string;
  reply_name: string;
  reply_email: string;
  bcc: string;
  attach_pdf: boolean;
  send_hour: number;
  templates: Record<MailKind, MailTemplate>;
  reminders: MailReminder[];
};

export const MAIL_KINDS: { id: MailKind; label: string; button: string }[] = [
  { id: "invoice", label: "Facture", button: "Voir la facture" },
  { id: "quote", label: "Devis", button: "Voir le devis" },
  { id: "credit_note", label: "Avoir", button: "Voir l’avoir" },
  { id: "reminder", label: "Rappel", button: "Voir la facture" },
  { id: "payment", label: "Reçu de paiement", button: "Voir la facture" },
];

export const MAIL_TOKENS: { id: string; label: string }[] = [
  { id: "{client}", label: "{client}" },
  { id: "{numero}", label: "{numero}" },
  { id: "{montant}", label: "{montant}" },
  { id: "{societe}", label: "{societe}" },
  { id: "{date}", label: "{date}" },
  { id: "{echeance}", label: "{echeance}" },
  { id: "{bouton}", label: "{bouton}" },
];

const TPL: Record<MailKind, MailTemplate> = {
  invoice: {
    subject: "Nouvelle facture {numero} de {societe}",
    body: "<p>Bonjour {client},</p><p>Votre facture d’un montant de {montant} est désormais disponible.<br />Pour la consulter et la télécharger, veuillez cliquer sur le bouton ci-dessous :</p><p>{bouton}</p><p>Merci pour votre confiance,<br />L’équipe {societe}</p>",
  },
  quote: {
    subject: "Devis {numero} de {societe}",
    body: "<p>Bonjour {client},</p><p>Votre devis d’un montant de {montant} est désormais disponible.<br />Pour le consulter, veuillez cliquer sur le bouton ci-dessous :</p><p>{bouton}</p><p>Bien cordialement,<br />L’équipe {societe}</p>",
  },
  credit_note: {
    subject: "Avoir {numero} de {societe}",
    body: "<p>Bonjour {client},</p><p>Un avoir d’un montant de {montant} a été établi.<br />Pour le consulter, veuillez cliquer sur le bouton ci-dessous :</p><p>{bouton}</p><p>Bien cordialement,<br />L’équipe {societe}</p>",
  },
  reminder: {
    subject: "Rappel — facture {numero} de {societe}",
    body: "<p>Bonjour {client},</p><p>Sauf erreur de notre part, la facture {numero} d’un montant de {montant} reste due.<br />Vous pouvez la consulter ci-dessous :</p><p>{bouton}</p><p>Merci de votre diligence,<br />L’équipe {societe}</p>",
  },
  payment: {
    subject: "Reçu de paiement {numero} — {societe}",
    body: "<p>Bonjour {client},</p><p>Nous avons bien enregistré votre paiement de {montant}.<br />Vous pouvez consulter la facture correspondante ci-dessous :</p><p>{bouton}</p><p>Merci pour votre confiance,<br />L’équipe {societe}</p>",
  },
};

const REM: MailReminder[] = [
  { enabled: true, days: 7 },
  { enabled: true, days: 15 },
  { enabled: false, days: 30 },
];

function asStr(v: unknown, fallback: string, max: number): string {
  const s = String(v ?? fallback);
  return s.slice(0, max);
}

function asBool(v: unknown, fallback: boolean): boolean {
  if (v == null) return fallback;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (v === "0" || v === "false") return false;
  return Boolean(v);
}

function asHour(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n <= 23 ? Math.trunc(n) : 6;
}

function asTpl(raw: unknown, fallback: MailTemplate): MailTemplate {
  const row = raw && typeof raw === "object" ? (raw as MailTemplate) : fallback;
  return {
    subject: asStr(row.subject, fallback.subject, 180) || fallback.subject,
    body: asStr(row.body, fallback.body, 20000) || fallback.body,
  };
}

export function mailDefaults(): CompanyMail {
  return {
    from_name: "",
    reply_name: "",
    reply_email: "",
    bcc: "",
    attach_pdf: true,
    send_hour: 6,
    templates: { ...TPL },
    reminders: REM.map((r) => ({ ...r })),
  };
}

export function mailFromCompany(row: { mail?: Partial<CompanyMail> | null } | null | undefined): CompanyMail {
  const d = mailDefaults();
  const src = row?.mail ?? {};
  const tpls = src.templates ?? {};
  const rem = Array.isArray(src.reminders) ? src.reminders : [];
  return {
    from_name: asStr(src.from_name, d.from_name, 80),
    reply_name: asStr(src.reply_name, d.reply_name, 80),
    reply_email: asStr(src.reply_email, d.reply_email, 255),
    bcc: asStr(src.bcc, d.bcc, 400),
    attach_pdf: asBool(src.attach_pdf, d.attach_pdf),
    send_hour: asHour(src.send_hour),
    templates: {
      invoice: asTpl(tpls.invoice, TPL.invoice),
      quote: asTpl(tpls.quote, TPL.quote),
      credit_note: asTpl(tpls.credit_note, TPL.credit_note),
      reminder: asTpl(tpls.reminder, TPL.reminder),
      payment: asTpl(tpls.payment, TPL.payment),
    },
    reminders: [0, 1, 2].map((i) => {
      const r = rem[i] ?? REM[i];
      const days = Number(r.days);
      return {
        enabled: asBool(r.enabled, REM[i].enabled),
        days: days >= 1 && days <= 365 ? Math.trunc(days) : REM[i].days,
      };
    }),
  };
}

export function fillMailText(raw: string, vars: Record<string, string>): string {
  const aliases: Record<string, string> = {
    "{client}": vars.client ?? "",
    "{numero}": vars.numero ?? "",
    "{montant}": vars.montant ?? "",
    "{societe}": vars.societe ?? "",
    "{bouton}": vars.bouton ?? "",
    "{date}": vars.date ?? "",
    "{echeance}": vars.echeance ?? "",
    "$client.name": vars.client ?? "",
    "$number": vars.numero ?? "",
    "$amount": vars.montant ?? "",
    "$company.name": vars.societe ?? "",
    "$view_button": vars.bouton ?? "",
  };
  let out = raw;
  for (const [k, v] of Object.entries(aliases)) {
    out = out.split(k).join(v);
  }
  return out;
}

export function mailHourLabel(h: number): string {
  return `${String(h).padStart(2, "0")}:00`;
}
